import { Component } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { FormArray, FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { BehaviorSubject, Observable, combineLatest, map, of, shareReplay, switchMap } from 'rxjs';

import { RegionsService, RegionDto } from '../../api/regions.service';
import { PointsOfInterestService, PoiDto } from '../../api/points-of-interest.service';
import { TypesService, WorldTypesResponse } from '../../api/types.service';
import { QuestsService, QuestDto } from '../../api/quests.service';
import { TomesService, TomeDto } from '../../api/tomes.service';
import { FactionsService, FactionDto } from '../../api/factions.service';
import { GodsService, GodDto } from '../../api/gods.service';
import { TomePagesService, TomePageDto } from '../../api/tome-pages.service';
import { QuestRequirementsService, QuestRequirementDto } from '../../api/quest-requirements.service';
import { NonPlayerCharactersService, NonPlayerCharacterDto } from '../../api/non-player-characters.service';
import { PlayerCharactersService, PlayerCharacterDto } from '../../api/player-characters.service';
import { PantheonsService, PantheonDto } from '../../api/pantheons.service';
import { DomainsService, DomainDto } from '../../api/domains.service';
import { EncountersService, EncounterDto } from '../../api/encounters.service';
import { ItemsService, ItemDto } from '../../api/items.service';
import { GoodsServicesService, GoodsServiceDto } from '../../api/goods-services.service';
import { MerchantsService, MerchantDto, MerchantInventoryDto } from '../../api/merchants.service';
import { AssetLinksService, AssetLinkWithAssetDto } from '../../api/asset-links.service';

@Component({
	selector: 'of-entity-editor-page',
	standalone: true,
	imports: [
		CommonModule,
		RouterModule,
		ReactiveFormsModule,
	],
	styleUrls: ['./entity-editor.page.scss'],
	template: `
		<section class="entity-editor-shell" *ngIf="vm$ | async as vm; else loading">
			<header class="entity-editor-header">
				<div>
					<p class="kicker">Workspace</p>
					<h2>{{ vm.title }}</h2>
				</div>
				<div class="header-actions">
					<button type="button" class="btn-outline" (click)="cancel()">Back</button>
				</div>
			</header>

			<div class="entity-editor-card">
				<ng-container [ngSwitch]="vm.kind">
					<form *ngSwitchCase="'regions'" [formGroup]="regionForm" (ngSubmit)="saveRegion()">
						<div class="entity-editor-panel">
							<div class="entity-editor-grid">
								<label class="forge-field wide">
									<span>Name</span>
									<input type="text" formControlName="name" />
								</label>
								<label class="forge-field wide">
									<span>Description</span>
									<textarea rows="4" formControlName="description"></textarea>
								</label>
							</div>

							<div class="entity-editor-actions">
								<button type="button" class="btn-outline" (click)="cancel()">Cancel</button>
								<button class="btn-primary-gradient" type="submit" [disabled]="regionForm.invalid || saving">
									{{ saving ? 'Saving...' : 'Save' }}
								</button>
							</div>
						</div>
					</form>

					<form *ngSwitchCase="'quests'" [formGroup]="questForm" (ngSubmit)="saveQuest()">
						<div class="entity-editor-panel">
							<div class="entity-editor-grid">
								<label class="forge-field wide">
									<span>Name</span>
									<input type="text" formControlName="name" />
								</label>
								<div class="triple">
									<label class="forge-field">
										<span>Status</span>
										<select formControlName="status">
											<option *ngFor="let s of $any(vm).questStatuses" [value]="s.id">{{ s.name }}</option>
										</select>
									</label>
									<label class="forge-field">
										<span>Foundry Id</span>
										<input type="text" formControlName="foundryId" />
									</label>
								</div>
								<div class="triple">
									<label class="forge-field">
										<span>Faction</span>
										<select formControlName="factionId">
											<option [value]="''">(none)</option>
											<option *ngFor="let f of $any(vm).factions" [value]="f.id">{{ f.name }}</option>
										</select>
									</label>
									<label class="forge-field">
										<span>NPC</span>
										<input type="text" formControlName="npcId" />
									</label>
									<label class="forge-field">
										<span>God</span>
										<select formControlName="godId">
											<option [value]="''">(none)</option>
											<option *ngFor="let g of $any(vm).gods" [value]="g.id">{{ g.name }}</option>
										</select>
									</label>
								</div>
								<label class="forge-field wide">
									<span>Description</span>
									<textarea rows="4" formControlName="description"></textarea>
								</label>
								<label class="forge-field wide">
									<span>GM Notes</span>
									<textarea rows="4" formControlName="gmNotes"></textarea>
								</label>
								<div class="triple">
									<label class="forge-field">
										<span>Image Asset Id</span>
										<input type="text" formControlName="imageAssetId" />
									</label>
									<label class="forge-field">
										<span>Map Asset Id</span>
										<input type="text" formControlName="mapAssetId" />
									</label>
									<label class="forge-field">
										<span>PDF Asset Id</span>
										<input type="text" formControlName="pdfAssetId" />
									</label>
								</div>
							</div>

							<div class="entity-editor-actions">
								<button type="button" class="btn-outline" (click)="cancel()">Cancel</button>
								<button class="btn-primary-gradient" type="submit" [disabled]="questForm.invalid || saving">
									{{ saving ? 'Saving...' : 'Save' }}
								</button>
							</div>
						</div>

						<div *ngIf="id" style="margin-top: 16px;">
							<div style="display:flex; align-items:center; gap: 12px;">
								<h4 style="margin: 0; flex: 1 1 auto;">Quest Requirements</h4>
								<button type="button" class="btn-outline" (click)="openNewQuestRequirement()">Add Requirement</button>
							</div>

							<ng-container *ngIf="$any(vm).questRequirements?.length > 0; else noQuestRequirements">
								<div *ngFor="let qr of $any(vm).questRequirements" style="padding: 8px 0;">
									<div style="display:flex; gap: 12px; align-items:center;">
										<div style="min-width:0; flex: 1 1 auto;">
											<div style="font-weight: 600;">{{ qr.name }}</div>
											<div class="muted" style="font-size: 13px;">{{ qr.status ?? 'Not Known' }}</div>
										</div>
										<div class="actions" style="margin: 0;">
											<button type="button" class="btn-outline" (click)="openQuestRequirement(qr.id)">Open</button>
										</div>
									</div>
								</div>
							</ng-container>
							<ng-template #noQuestRequirements>
								<p class="muted" style="margin: 8px 0 0 0;">No quest requirements yet.</p>
							</ng-template>
						</div>
					</form>

					<form *ngSwitchCase="'tomes'" [formGroup]="tomeForm" (ngSubmit)="saveTome()">
						<div class="entity-editor-panel">
							<div class="entity-editor-grid">
								<label class="forge-field wide">
									<span>Name</span>
									<input type="text" formControlName="name" />
								</label>
								<label class="forge-field wide">
									<span>Description</span>
									<textarea rows="4" formControlName="description"></textarea>
								</label>
								<div class="triple">
									<label class="forge-field">
										<span>Point of Interest</span>
										<select formControlName="poiId">
											<option [value]="''">(none)</option>
											<option *ngFor="let p of $any(vm).pois" [value]="p.id">
												{{ p.name }}{{ p.regionName ? ' · ' + p.regionName : '' }}
											</option>
										</select>
									</label>
									<label class="forge-field">
										<span>Rarity</span>
										<select formControlName="rarity">
											<option [value]="''">(none)</option>
											<option *ngFor="let r of $any(vm).itemRarities" [value]="r.id">{{ r.name }}</option>
										</select>
									</label>
									<label class="forge-field">
										<span>Author</span>
										<input type="text" formControlName="author" />
									</label>
								</div>
								<div class="triple">
									<label class="forge-field">
										<span>Foundry Id</span>
										<input type="text" formControlName="foundryId" />
									</label>
									<label class="forge-field">
										<span>Image Asset Id</span>
										<input type="text" formControlName="imageAssetId" />
									</label>
									<label class="forge-field">
										<span>Map Asset Id</span>
										<input type="text" formControlName="mapAssetId" />
									</label>
								</div>
								<label class="forge-field wide">
									<span>PDF Asset Id</span>
									<input type="text" formControlName="pdfAssetId" />
								</label>
							</div>

							<div class="entity-editor-actions">
								<button type="button" class="btn-outline" (click)="cancel()">Cancel</button>
								<button class="btn-primary-gradient" type="submit" [disabled]="tomeForm.invalid || saving">
									{{ saving ? 'Saving...' : 'Save' }}
								</button>
							</div>
						</div>

						<div *ngIf="id" class="entity-editor-panel">
							<div class="entity-editor-grid" style="gap: 8px;">
								<div style="display:flex; align-items:center; gap: 12px;">
									<h4 style="margin: 0; flex: 1 1 auto;">Tome Pages</h4>
									<button type="button" class="btn-outline" (click)="openNewTomePage()">Add Page</button>
								</div>

								<ng-container *ngIf="$any(vm).tomePages?.length > 0; else noTomePages">
									<div *ngFor="let tp of $any(vm).tomePages" style="padding: 8px 0;">
										<div style="display:flex; gap: 12px; align-items:center;">
											<div style="min-width:0; flex: 1 1 auto;">
												<div style="font-weight: 600;">{{ tp.name }}</div>
												<div class="muted" style="font-size: 13px;">Updated {{ tp.updatedAt | date:'short' }}</div>
											</div>
											<div class="entity-editor-actions" style="margin: 0; justify-content: flex-start;">
												<button type="button" class="btn-outline" (click)="openTomePage(tp.id)">Open</button>
											</div>
										</div>
									</div>
								</ng-container>
								<ng-template #noTomePages>
									<p class="muted" style="margin: 8px 0 0 0;">No tome pages yet.</p>
								</ng-template>
							</div>
						</div>
					</form>

					<form *ngSwitchCase="'tome-pages'" [formGroup]="tomePageForm" (ngSubmit)="saveTomePage()">
						<div class="entity-editor-panel">
							<div class="entity-editor-grid">
								<label class="forge-field wide">
									<span>Tome</span>
									<select formControlName="tomeId">
										<option *ngFor="let t of $any(vm).tomes" [value]="t.id">{{ t.name }}</option>
									</select>
								</label>
								<label class="forge-field wide">
									<span>Name</span>
									<input type="text" formControlName="name" />
								</label>
								<label class="forge-field wide">
									<span>Content</span>
									<textarea rows="10" formControlName="content"></textarea>
								</label>
								<div class="triple">
									<label class="forge-field">
										<span>Foundry Id</span>
										<input type="text" formControlName="foundryId" />
									</label>
									<label class="forge-field">
										<span>Image Asset Id</span>
										<input type="text" formControlName="imageAssetId" />
									</label>
								</div>
								<p *ngIf="$any(vm).tomeName" class="muted" style="margin-top: 0;">Tome: {{ $any(vm).tomeName }}</p>
							</div>

							<div class="entity-editor-actions">
								<button type="button" class="btn-outline" (click)="cancel()">Cancel</button>
								<button class="btn-primary-gradient" type="submit" [disabled]="tomePageForm.invalid || saving">
									{{ saving ? 'Saving...' : 'Save' }}
								</button>
							</div>
						</div>
					</form>

					<form *ngSwitchCase="'quest-requirements'" [formGroup]="questRequirementForm" (ngSubmit)="saveQuestRequirement()">
						<div class="entity-editor-panel">
							<div class="entity-editor-grid">
								<label class="forge-field wide">
									<span>Quest</span>
									<select formControlName="questId">
										<option *ngFor="let q of $any(vm).quests" [value]="q.id">{{ q.name }}</option>
									</select>
								</label>
								<label class="forge-field wide">
									<span>Name</span>
									<input type="text" formControlName="name" />
								</label>
								<label class="forge-field wide">
									<span>Description</span>
									<textarea rows="4" formControlName="description"></textarea>
								</label>
								<div class="triple">
									<label class="forge-field">
										<span>Status</span>
										<input type="text" formControlName="status" />
									</label>
									<label class="forge-field">
										<span>Foundry Id</span>
										<input type="text" formControlName="foundryId" />
									</label>
									<label class="forge-field">
										<span>Image Asset Id</span>
										<input type="text" formControlName="imageAssetId" />
									</label>
								</div>
								<p *ngIf="$any(vm).questName" class="muted" style="margin-top: 0;">Quest: {{ $any(vm).questName }}</p>
							</div>

							<div class="entity-editor-actions">
								<button type="button" class="btn-outline" (click)="cancel()">Cancel</button>
								<button class="btn-primary-gradient" type="submit" [disabled]="questRequirementForm.invalid || saving">
									{{ saving ? 'Saving...' : 'Save' }}
								</button>
							</div>
						</div>
					</form>

					<form *ngSwitchCase="'non-player-characters'" [formGroup]="npcForm" (ngSubmit)="saveNpc()">
						<div class="entity-editor-panel">
							<div class="entity-editor-grid">
								<label class="forge-field wide">
									<span>Name</span>
									<input type="text" formControlName="name" />
								</label>
								<div class="triple">
									<label class="forge-field">
										<span>Role</span>
										<input type="text" formControlName="role" />
									</label>
									<label class="forge-field">
										<span>Disposition Scale</span>
										<input type="text" formControlName="dispositionScale" />
									</label>
									<label class="forge-field">
										<span>Last Seen Session</span>
										<input type="text" formControlName="lastSeenSession" />
									</label>
								</div>
								<div class="triple">
									<label class="forge-field">
										<span>Home Point of Interest</span>
										<select formControlName="homePoiId">
											<option [value]="''">(none)</option>
											<option *ngFor="let p of $any(vm).pois" [value]="p.id">
												{{ p.name }}{{ p.regionName ? ' · ' + p.regionName : '' }}
											</option>
										</select>
									</label>
									<label class="forge-field">
										<span>Faction</span>
										<select formControlName="factionId">
											<option [value]="''">(none)</option>
											<option *ngFor="let f of $any(vm).factions" [value]="f.id">{{ f.name }}</option>
										</select>
									</label>
									<label class="forge-field">
										<span>Affiliation</span>
										<input type="text" formControlName="affiliation" />
									</label>
								</div>
								<label class="forge-field wide">
									<span>Description</span>
									<textarea rows="4" formControlName="description"></textarea>
								</label>
								<div class="triple">
									<label class="forge-field">
										<span>Goal</span>
										<textarea rows="3" formControlName="goal"></textarea>
									</label>
									<label class="forge-field">
										<span>Secret</span>
										<textarea rows="3" formControlName="secret"></textarea>
									</label>
									<label class="forge-field">
										<span>GM Notes</span>
										<textarea rows="3" formControlName="gmNotes"></textarea>
									</label>
								</div>
								<div class="triple">
									<label class="forge-field">
										<span>Appearance Notes</span>
										<textarea rows="3" formControlName="appearanceNotes"></textarea>
									</label>
									<label class="forge-field">
										<span>Voice Notes</span>
										<textarea rows="3" formControlName="voiceNotes"></textarea>
									</label>
									<label class="forge-field">
										<span>Foundry Id</span>
										<input type="text" formControlName="foundryId" />
									</label>
								</div>
								<label class="forge-field wide">
									<span>Image Asset Id</span>
									<input type="text" formControlName="imageAssetId" />
								</label>
							</div>

							<div class="entity-editor-actions">
								<button type="button" class="btn-outline" (click)="cancel()">Cancel</button>
								<button class="btn-primary-gradient" type="submit" [disabled]="npcForm.invalid || saving">
									{{ saving ? 'Saving...' : 'Save' }}
								</button>
							</div>
						</div>
					</form>

					<form *ngSwitchCase="'player-characters'" [formGroup]="playerCharacterForm" (ngSubmit)="savePlayerCharacter()">
						<div class="entity-editor-panel">
							<div class="entity-editor-grid">
								<label class="forge-field wide">
									<span>Name</span>
									<input type="text" formControlName="name" />
								</label>
								<div class="triple">
									<label class="forge-field">
										<span>Status</span>
										<select formControlName="status">
											<option *ngFor="let s of $any(vm).characterStatuses" [value]="s.id">{{ s.name }}</option>
										</select>
									</label>
									<label class="forge-field">
										<span>User Id</span>
										<input type="text" formControlName="userId" />
									</label>
									<label class="forge-field">
										<span>Foundry Id</span>
										<input type="text" formControlName="foundryId" />
									</label>
								</div>
								<div class="triple">
									<label class="forge-field">
										<span>Image Asset Id</span>
										<input type="text" formControlName="imageAssetId" />
									</label>
									<label class="forge-field">
										<span>Motivation</span>
										<input type="text" formControlName="motivation" />
									</label>
								</div>
								<label class="forge-field wide">
									<span>Backstory</span>
									<textarea rows="6" formControlName="backstory"></textarea>
								</label>
							</div>

							<div class="entity-editor-actions">
								<button type="button" class="btn-outline" (click)="cancel()">Cancel</button>
								<button class="btn-primary-gradient" type="submit" [disabled]="playerCharacterForm.invalid || saving">
									{{ saving ? 'Saving...' : 'Save' }}
								</button>
							</div>
						</div>
					</form>

					<form *ngSwitchCase="'factions'" [formGroup]="factionForm" (ngSubmit)="saveFaction()">
						<div class="entity-editor-panel">
							<div class="entity-editor-grid">
								<label class="forge-field wide">
									<span>Name</span>
									<input type="text" formControlName="name" />
								</label>
								<label class="forge-field wide">
									<span>Description</span>
									<textarea rows="4" formControlName="description"></textarea>
								</label>
								<label class="forge-field wide">
									<span>Influence</span>
									<textarea rows="3" formControlName="influence"></textarea>
								</label>
								<label class="forge-field wide">
									<span>Resources</span>
									<textarea rows="3" formControlName="resources"></textarea>
								</label>
								<label class="forge-field wide">
									<span>Operational Regions</span>
									<textarea rows="3" formControlName="operationalRegions"></textarea>
								</label>
								<label class="forge-field wide">
									<span>Opposition</span>
									<textarea rows="3" formControlName="opposition"></textarea>
								</label>
								<div class="triple">
									<label class="forge-field">
										<span>Regions</span>
										<select formControlName="regionIds" multiple>
											<option *ngFor="let r of $any(vm).regions" [value]="r.id">{{ r.name }}</option>
										</select>
									</label>
									<label class="forge-field">
										<span>Points of Interest</span>
										<select formControlName="poiIds" multiple>
											<option *ngFor="let p of $any(vm).pois" [value]="p.id">
												{{ p.name }}{{ p.regionName ? ' · ' + p.regionName : '' }}
											</option>
										</select>
									</label>
									<label class="forge-field">
										<span>NPC Members</span>
										<select formControlName="npcIds" multiple>
											<option *ngFor="let n of $any(vm).npcs" [value]="n.id">{{ n.name }}</option>
										</select>
									</label>
								</div>
								<div class="triple">
									<label class="forge-field">
										<span>Icon Asset Id</span>
										<input type="text" formControlName="iconAssetId" />
									</label>
									<label class="forge-field">
										<span>Foundry Id</span>
										<input type="text" formControlName="foundryId" />
									</label>
								</div>
							</div>

							<div class="entity-editor-actions">
								<button type="button" class="btn-outline" (click)="cancel()">Cancel</button>
								<button class="btn-primary-gradient" type="submit" [disabled]="factionForm.invalid || saving">
									{{ saving ? 'Saving...' : 'Save' }}
								</button>
							</div>
						</div>
					</form>

					<form *ngSwitchCase="'gods'" [formGroup]="godForm" (ngSubmit)="saveGod()">
						<div class="entity-editor-panel">
							<div class="entity-editor-grid">
								<div class="triple">
									<label class="forge-field">
										<span>Pantheon Id</span>
										<input type="text" formControlName="pantheonId" />
									</label>
									<label class="forge-field">
										<span>Name</span>
										<input type="text" formControlName="name" />
									</label>
								</div>
								<label class="forge-field wide">
									<span>Description</span>
									<textarea rows="4" formControlName="description"></textarea>
								</label>
								<div class="triple">
									<label class="forge-field">
										<span>Follower Count</span>
										<input type="number" formControlName="followerCount" />
									</label>
									<label class="forge-field">
										<span>Foundry Id</span>
										<input type="text" formControlName="foundryId" />
									</label>
									<label class="forge-field">
										<span>Image Asset Id</span>
										<input type="text" formControlName="imageAssetId" />
									</label>
								</div>
								<label class="forge-field wide">
									<span>Domain Ids (comma/newline separated)</span>
									<textarea rows="2" formControlName="domainIdsCsv"></textarea>
								</label>
								<label class="forge-field wide">
									<span>Symbols</span>
									<textarea rows="2" formControlName="symbols"></textarea>
								</label>
								<label class="forge-field wide">
									<span>Tenets</span>
									<textarea rows="3" formControlName="tenets"></textarea>
								</label>
								<div class="triple">
									<label class="forge-field">
										<span>Boons</span>
										<textarea rows="3" formControlName="boons"></textarea>
									</label>
									<label class="forge-field">
										<span>Banes</span>
										<textarea rows="3" formControlName="banes"></textarea>
									</label>
									<label class="forge-field">
										<span>Temple Regions</span>
										<textarea rows="3" formControlName="templeRegions"></textarea>
									</label>
								</div>
								<label class="forge-field wide">
									<span>Relationship Map</span>
									<textarea rows="4" formControlName="relationshipMap"></textarea>
								</label>
							</div>

							<div class="entity-editor-actions">
								<button type="button" class="btn-outline" (click)="cancel()">Cancel</button>
								<button class="btn-primary-gradient" type="submit" [disabled]="godForm.invalid || saving">
									{{ saving ? 'Saving...' : 'Save' }}
								</button>
							</div>
						</div>
					</form>

					<form *ngSwitchCase="'pantheons'" [formGroup]="pantheonForm" (ngSubmit)="savePantheon()">
						<div class="entity-editor-panel">
							<div class="entity-editor-grid">
								<label class="forge-field wide">
									<span>Name</span>
									<input type="text" formControlName="name" />
								</label>
								<label class="forge-field wide">
									<span>Current State</span>
									<textarea rows="3" formControlName="currentState"></textarea>
								</label>
								<label class="forge-field wide">
									<span>Historical State</span>
									<textarea rows="3" formControlName="historicalState"></textarea>
								</label>
								<label class="forge-field wide">
									<span>Holy Days</span>
									<textarea rows="2" formControlName="holyDays"></textarea>
								</label>
								<div class="triple">
									<label class="forge-field">
										<span>Iconography Asset Id</span>
										<input type="text" formControlName="iconographyAssetId" />
									</label>
									<label class="forge-field">
										<span>Foundry Id</span>
										<input type="text" formControlName="foundryId" />
									</label>
								</div>
							</div>

							<div class="entity-editor-actions">
								<button type="button" class="btn-outline" (click)="cancel()">Cancel</button>
								<button class="btn-primary-gradient" type="submit" [disabled]="pantheonForm.invalid || saving">
									{{ saving ? 'Saving...' : 'Save' }}
								</button>
							</div>
						</div>
					</form>

					<form *ngSwitchCase="'domains'" [formGroup]="domainForm" (ngSubmit)="saveDomain()">
						<div class="entity-editor-panel">
							<div class="entity-editor-grid">
								<label class="forge-field wide">
									<span>Pantheon</span>
									<select formControlName="pantheonId" required>
										<option *ngFor="let p of ($any(vm).pantheons ?? [])" [value]="p.id">{{ p.name }}</option>
									</select>
								</label>
								<label class="forge-field wide">
									<span>Name</span>
									<input type="text" formControlName="name" required />
								</label>
								<label class="forge-field wide">
									<span>Description</span>
									<textarea rows="4" formControlName="description"></textarea>
								</label>
								<div class="triple">
									<label class="forge-field">
										<span>Symbol</span>
										<input type="text" formControlName="symbol" />
									</label>
									<label class="forge-field">
										<span>Image Asset Id</span>
										<input type="text" formControlName="imageAssetId" />
									</label>
									<label class="forge-field">
										<span>Foundry Id</span>
										<input type="text" formControlName="foundryId" />
									</label>
								</div>
								<label class="forge-field wide">
									<span>Tenets</span>
									<textarea rows="4" formControlName="tenets"></textarea>
								</label>
							</div>

							<div class="entity-editor-actions">
								<button type="button" class="btn-outline" (click)="cancel()">Cancel</button>
								<button class="btn-primary-gradient" type="submit" [disabled]="domainForm.invalid || saving">
									{{ saving ? 'Saving...' : 'Save' }}
								</button>
							</div>
						</div>
					</form>

					<form *ngSwitchCase="'encounters'" [formGroup]="encounterForm" (ngSubmit)="saveEncounter()">
						<div class="entity-editor-panel">
							<div class="entity-editor-grid">
								<div class="triple">
									<label class="forge-field">
										<span>Quest</span>
										<select formControlName="questId">
											<option [value]="''">(none)</option>
											<option *ngFor="let q of ($any(vm).quests ?? [])" [value]="q.id">{{ q.name }}</option>
										</select>
									</label>
									<label class="forge-field">
										<span>Point of Interest</span>
										<select formControlName="poiId">
											<option [value]="''">(none)</option>
											<option *ngFor="let p of ($any(vm).pois ?? [])" [value]="p.id">{{ p.name }}</option>
										</select>
									</label>
									<label class="forge-field">
										<span>Name</span>
										<input type="text" formControlName="name" />
									</label>
								</div>
								<label class="forge-field wide">
									<span>Environment</span>
									<textarea rows="3" formControlName="environment"></textarea>
								</label>
								<label class="forge-field wide">
									<span>Creatures</span>
									<textarea rows="3" formControlName="creatures"></textarea>
								</label>
								<label class="forge-field wide">
									<span>Treasure</span>
									<textarea rows="3" formControlName="treasure"></textarea>
								</label>
								<label class="forge-field wide">
									<span>Escalation</span>
									<textarea rows="3" formControlName="escalation"></textarea>
								</label>
								<label class="forge-field wide">
									<span>Aftermath</span>
									<textarea rows="3" formControlName="aftermath"></textarea>
								</label>
								<div class="triple">
									<label class="forge-field">
										<span>Image Asset Id</span>
										<input type="text" formControlName="imageAssetId" />
									</label>
									<label class="forge-field">
										<span>Map Asset Id</span>
										<input type="text" formControlName="mapAssetId" />
									</label>
									<label class="forge-field">
										<span>PDF Asset Id</span>
										<input type="text" formControlName="pdfAssetId" />
									</label>
								</div>
								<label class="forge-field wide">
									<span>Foundry Id</span>
									<input type="text" formControlName="foundryId" />
								</label>
							</div>

							<div class="entity-editor-actions">
								<button type="button" class="btn-outline" (click)="cancel()">Cancel</button>
								<button class="btn-primary-gradient" type="submit" [disabled]="encounterForm.invalid || saving">
									{{ saving ? 'Saving...' : 'Save' }}
								</button>
							</div>
						</div>
					</form>

					<form *ngSwitchCase="'items'" [formGroup]="itemForm" (ngSubmit)="saveItem()">
						<div class="entity-editor-panel">
							<div class="entity-editor-grid">
								<div class="triple">
									<label class="forge-field">
										<span>Item Type</span>
										<select formControlName="itemTypeId">
											<option [value]="''">(none)</option>
											<option *ngFor="let t of ($any(vm).itemTypes ?? [])" [value]="t.id">{{ t.name }}</option>
										</select>
									</label>
									<label class="forge-field">
										<span>Source POI</span>
										<select formControlName="sourcePoiId">
											<option [value]="''">(none)</option>
											<option *ngFor="let p of ($any(vm).pois ?? [])" [value]="p.id">{{ p.name }}</option>
										</select>
									</label>
									<label class="forge-field">
										<span>Rarity</span>
										<select formControlName="rarity">
											<option [value]="''">(none)</option>
											<option *ngFor="let r of ($any(vm).itemRarities ?? [])" [value]="r.id">{{ r.name }}</option>
										</select>
									</label>
								</div>
								<label class="forge-field wide">
									<span>Name</span>
									<input type="text" formControlName="name" />
								</label>
								<div class="triple">
									<label class="forge-field">
										<span>Category</span>
										<input type="text" formControlName="category" />
									</label>
									<label class="forge-field">
										<span>Image Asset Id</span>
										<input type="text" formControlName="imageAssetId" />
									</label>
									<label class="forge-field">
										<span>Foundry Id</span>
										<input type="text" formControlName="foundryId" />
									</label>
								</div>
								<label class="forge-field wide">
									<span>Description</span>
									<textarea rows="4" formControlName="description"></textarea>
								</label>
								<label class="forge-field wide">
									<span>GM Notes</span>
									<textarea rows="3" formControlName="gmNotes"></textarea>
								</label>
								<label class="forge-field wide">
									<span>Tags (comma-separated)</span>
									<input type="text" formControlName="tagsCsv" />
								</label>
							</div>

							<div class="entity-editor-actions">
								<button type="button" class="btn-outline" (click)="cancel()">Cancel</button>
								<button class="btn-primary-gradient" type="submit" [disabled]="itemForm.invalid || saving">
									{{ saving ? 'Saving...' : 'Save' }}
								</button>
							</div>
						</div>
					</form>

					<form *ngSwitchCase="'goods-services'" [formGroup]="goodsServiceForm" (ngSubmit)="saveGoodsService()">
						<div class="entity-editor-panel">
							<div class="entity-editor-grid">
								<label class="forge-field wide">
									<span>Name</span>
									<input type="text" formControlName="name" />
								</label>
								<div class="triple">
									<label class="forge-field">
										<span>Category</span>
										<input type="text" formControlName="category" />
									</label>
									<label class="forge-field">
										<span>Rarity</span>
										<input type="text" formControlName="rarity" />
									</label>
									<label class="forge-field">
										<span>Attunement Required</span>
										<select formControlName="attunementRequired">
											<option [value]="false">No</option>
											<option [value]="true">Yes</option>
										</select>
									</label>
								</div>
								<div class="triple">
									<label class="forge-field">
										<span>Cost</span>
										<input type="text" formControlName="cost" />
									</label>
									<label class="forge-field">
										<span>Currency</span>
										<input type="text" formControlName="currency" />
									</label>
									<label class="forge-field">
										<span>Availability</span>
										<input type="text" formControlName="availability" />
									</label>
								</div>
								<label class="forge-field wide">
									<span>Description</span>
									<textarea rows="4" formControlName="description"></textarea>
								</label>
								<label class="forge-field wide">
									<span>Mechanical Effect</span>
									<textarea rows="3" formControlName="mechanicalEffect"></textarea>
								</label>
								<div class="triple">
									<label class="forge-field">
										<span>Charges</span>
										<input type="text" formControlName="charges" />
									</label>
									<label class="forge-field">
										<span>Source POI</span>
										<select formControlName="sourcePoiId">
											<option [value]="''">(none)</option>
											<option *ngFor="let p of ($any(vm).pois ?? [])" [value]="p.id">{{ p.name }}</option>
										</select>
									</label>
									<label class="forge-field">
										<span>Image Asset Id</span>
										<input type="text" formControlName="imageAssetId" />
									</label>
								</div>
								<label class="forge-field wide">
									<span>Foundry Id</span>
									<input type="text" formControlName="foundryId" />
								</label>
							</div>

							<div class="entity-editor-actions">
								<button type="button" class="btn-outline" (click)="cancel()">Cancel</button>
								<button class="btn-primary-gradient" type="submit" [disabled]="goodsServiceForm.invalid || saving">
									{{ saving ? 'Saving...' : 'Save' }}
								</button>
							</div>
						</div>
					</form>

					<form *ngSwitchCase="'merchants'" [formGroup]="merchantForm" (ngSubmit)="saveMerchant()">
						<div class="entity-editor-panel">
							<div class="entity-editor-grid">
								<label class="forge-field wide">
									<span>Name</span>
									<input type="text" formControlName="name" />
								</label>
								<label class="forge-field wide">
									<span>Description</span>
									<textarea rows="4" formControlName="description"></textarea>
								</label>
								<div class="triple">
									<label class="forge-field">
										<span>NPC</span>
										<select formControlName="npcId">
											<option [value]="''">(none)</option>
											<option *ngFor="let n of ($any(vm).npcs ?? [])" [value]="n.id">{{ n.name }}</option>
										</select>
									</label>
									<label class="forge-field">
										<span>POI</span>
										<select formControlName="poiId">
											<option [value]="''">(none)</option>
											<option *ngFor="let p of ($any(vm).pois ?? [])" [value]="p.id">{{ p.name }}</option>
										</select>
									</label>
									<label class="forge-field">
										<span>Faction</span>
										<select formControlName="factionId">
											<option [value]="''">(none)</option>
											<option *ngFor="let f of ($any(vm).factions ?? [])" [value]="f.id">{{ f.name }}</option>
										</select>
									</label>
								</div>
								<div class="triple">
									<label class="forge-field">
										<span>Image Asset Id</span>
										<input type="text" formControlName="imageAssetId" />
									</label>
									<label class="forge-field">
										<span>Foundry Id</span>
										<input type="text" formControlName="foundryId" />
									</label>
								</div>
							</div>

							<div class="entity-editor-grid" style="gap: 10px;">
								<h4 style="margin: 8px 0;">Inventory</h4>
								<div formArrayName="inventory" style="display:flex; flex-direction:column; gap: 12px;">
									<div class="inventory-row" *ngFor="let row of merchantInventory.controls; let i = index" [formGroupName]="i">
										<div class="triple">
											<label class="forge-field">
												<span>Goods</span>
												<select formControlName="goodsId">
													<option *ngFor="let g of ($any(vm).goods ?? [])" [value]="g.id">{{ g.name }}</option>
												</select>
											</label>
											<label class="forge-field">
												<span>Quantity</span>
												<input type="number" formControlName="quantity" />
											</label>
											<label class="forge-field">
												<span>Price Override</span>
												<input type="text" formControlName="priceOverride" />
											</label>
										</div>
										<label class="forge-field wide">
											<span>Notes</span>
											<textarea rows="2" formControlName="notes"></textarea>
										</label>
										<div class="entity-editor-actions" style="justify-content: flex-end;">
											<button type="button" class="btn-outline" (click)="removeMerchantInventoryRow(i)">Remove</button>
										</div>
									</div>

									<div>
										<button type="button" class="btn-outline" (click)="addMerchantInventoryRow()">Add Item</button>
									</div>
								</div>
							</div>

							<div class="entity-editor-actions">
								<button type="button" class="btn-outline" (click)="cancel()">Cancel</button>
								<button class="btn-primary-gradient" type="submit" [disabled]="merchantForm.invalid || saving">
									{{ saving ? 'Saving...' : 'Save' }}
								</button>
							</div>
						</div>
					</form>

					<form *ngSwitchCase="'points-of-interest'" [formGroup]="poiForm" (ngSubmit)="savePoi()">
						<div class="entity-editor-panel">
							<div class="entity-editor-grid">
								<label class="forge-field wide">
									<span>Name</span>
									<input type="text" formControlName="name" />
								</label>
								<label class="forge-field wide">
									<span>Description</span>
									<textarea rows="4" formControlName="description"></textarea>
								</label>
								<div class="double">
									<label class="forge-field">
										<span>Region</span>
										<select formControlName="regionId">
											<option *ngFor="let r of $any(vm).regions" [value]="r.id">{{ r.name }}</option>
										</select>
									</label>
									<label class="forge-field">
										<span>Type</span>
										<select formControlName="typeId">
											<option [value]="''">(none)</option>
											<option *ngFor="let t of $any(vm).poiTypes" [value]="t.id">{{ t.name }}</option>
										</select>
									</label>
								</div>
							</div>

							<div class="entity-editor-actions">
								<button type="button" class="btn-outline" (click)="cancel()">Cancel</button>
								<button class="btn-primary-gradient" type="submit" [disabled]="poiForm.invalid || saving">
									{{ saving ? 'Saving...' : 'Save' }}
								</button>
							</div>
						</div>
					</form>

					<div *ngSwitchDefault>
						<p class="muted">Editor not implemented yet for: {{ entity }}</p>
					</div>
				</ng-container>

				<div *ngIf="id" class="entity-editor-panel">
					<div style="display:flex; align-items:center; gap: 12px;">
						<h4 style="margin: 0; flex: 1 1 auto;">Media Links</h4>
						<a class="btn-outline" [routerLink]="['/workspace/worlds', worldId, 'media']">Open Media Library</a>
						<button class="btn-outline" type="button" (click)="refreshAssetLinks()">Refresh</button>
					</div>

					<form [formGroup]="assetLinkForm" (ngSubmit)="addAssetLink()" class="entity-editor-grid">
						<div class="triple">
							<label class="forge-field">
								<span>Asset ID</span>
								<input type="text" formControlName="assetId" />
							</label>
							<label class="forge-field">
								<span>Usage</span>
								<select formControlName="usage">
									<option value="image">Image</option>
									<option value="map">Map</option>
									<option value="pdf">PDF</option>
									<option value="icon">Icon</option>
								</select>
							</label>
							<label class="forge-field">
								<span>Sort</span>
								<input type="number" formControlName="sort" />
							</label>
						</div>

						<div class="entity-editor-actions">
							<button class="btn-primary-gradient" type="submit" [disabled]="assetLinkForm.invalid || savingAssetLink">
								{{ savingAssetLink ? 'Linking...' : 'Link Asset' }}
							</button>
						</div>
					</form>

					<div *ngIf="assetLinksVm$ | async as links">
						<div *ngIf="links.length > 0; else noLinks">
							<div *ngFor="let row of links" class="linked-asset-row">
								<div style="min-width:0; flex: 1 1 auto;">
									<div style="font-weight: 600;">{{ row.asset.name ?? '(unnamed)' }}</div>
									<div class="muted" style="font-size: 13px;">
										{{ row.link.usage }} · {{ row.asset.type }} · Updated {{ row.asset.updatedAt | date:'short' }}
									</div>
									<div class="muted" style="font-size: 12px; word-break: break-all;">Asset {{ row.asset.id }}</div>
								</div>
								<div class="entity-editor-actions" style="margin:0; justify-content:flex-end;">
									<a class="btn-outline" [href]="row.asset.url" target="_blank" rel="noreferrer">Open</a>
									<button type="button" class="btn-outline" (click)="unlinkAsset(row.link.id)">Unlink</button>
								</div>
							</div>
						</div>

						<ng-template #noLinks>
							<div class="muted" style="padding: 12px 0;">No linked assets.</div>
						</ng-template>
					</div>
				</div>
			</div>
		</section>

		<ng-template #loading>
			<section class="entity-editor-shell">
				<p class="support-text">Loading...</p>
			</section>
		</ng-template>
	`,
})
export class EntityEditorPage {
	entity: string;
	id: string | null;
	worldId: string;
	queryTomeId: string | null = null;
	queryQuestId: string | null = null;
	saving = false;
	savingAssetLink = false;

	assetLinkForm = this.fb.group({
		assetId: ['', [Validators.required]],
		usage: ['image', [Validators.required]],
		sort: [0],
	});

	private readonly assetLinksRefresh$ = new BehaviorSubject<void>(undefined);
	assetLinksVm$: Observable<AssetLinkWithAssetDto[]>;

	regionForm = this.fb.group({
		name: ['', [Validators.required, Validators.maxLength(200)]],
		description: [''],
	});

	poiForm = this.fb.group({
		name: ['', [Validators.required, Validators.maxLength(200)]],
		description: [''],
		regionId: ['', [Validators.required]],
		typeId: [''],
		gmNotes: [''],
	});

	questForm = this.fb.group({
		name: ['', [Validators.required, Validators.maxLength(200)]],
		status: ['planned', [Validators.required]],
		factionId: [''],
		npcId: [''],
		godId: [''],
		description: [''],
		gmNotes: [''],
		foundryId: [''],
		imageAssetId: [''],
		mapAssetId: [''],
		pdfAssetId: [''],
	});

	questRequirementForm = this.fb.group({
		questId: ['', [Validators.required]],
		name: ['', [Validators.required, Validators.maxLength(200)]],
		description: [''],
		status: ['Not Known', [Validators.required]],
		foundryId: [''],
		imageAssetId: [''],
	});

	tomeForm = this.fb.group({
		name: ['', [Validators.required, Validators.maxLength(200)]],
		description: [''],
		author: [''],
		rarity: [''],
		poiId: [''],
		foundryId: [''],
		imageAssetId: [''],
		mapAssetId: [''],
		pdfAssetId: [''],
	});

	tomePageForm = this.fb.group({
		tomeId: ['', [Validators.required]],
		name: ['', [Validators.required, Validators.maxLength(200)]],
		content: [''],
		foundryId: [''],
		imageAssetId: [''],
	});

	npcForm = this.fb.group({
		name: ['', [Validators.required, Validators.maxLength(200)]],
		description: [''],
		affiliation: [''],
		homePoiId: [''],
		factionId: [''],
		gmNotes: [''],
		foundryId: [''],
		imageAssetId: [''],
	});

	playerCharacterForm = this.fb.group({
		name: ['', [Validators.required, Validators.maxLength(200)]],
		status: ['active', [Validators.required]],
		userId: [''],
		foundryId: [''],
		imageAssetId: [''],
		backstory: [''],
		motivation: [''],
	});

	factionForm = this.fb.group({
		name: ['', [Validators.required, Validators.maxLength(200)]],
		description: [''],
		influence: [''],
		resources: [''],
		opposition: [''],
		gmNotes: [''],
		iconAssetId: [''],
		foundryId: [''],
		regionIds: [[] as string[]],
		poiIds: [[] as string[]],
		npcIds: [[] as string[]],
	});

	godForm = this.fb.group({
		pantheonId: ['', [Validators.required]],
		name: ['', [Validators.required, Validators.maxLength(200)]],
		description: [''],
		gmNotes: [''],
		imageAssetId: [''],
		domainIdsCsv: [''],
		foundryId: [''],
	});

	pantheonForm = this.fb.group({
		name: ['', [Validators.required, Validators.maxLength(200)]],
		currentState: [''],
		historicalState: [''],
		description: [''],
		gmNotes: [''],
		iconographyAssetId: [''],
		foundryId: [''],
	});

	domainForm = this.fb.group({
		pantheonId: ['', [Validators.required]],
		name: ['', [Validators.required, Validators.maxLength(200)]],
		description: [''],
		symbol: [''],
		imageAssetId: [''],
		tenets: [''],
		foundryId: [''],
	});

	encounterForm = this.fb.group({
		name: ['', [Validators.required, Validators.maxLength(200)]],
		description: [''],
		gmNotes: [''],
		imageAssetId: [''],
		mapAssetId: [''],
		pdfAssetId: [''],
		environment: [''],
		escalation: [''],
		aftermath: [''],
		foundryId: [''],
	});

	itemForm = this.fb.group({
		itemTypeId: [''],
		sourcePoiId: [''],
		rarity: [''],
		name: ['', [Validators.required, Validators.maxLength(200)]],
		category: [''],
		description: [''],
		gmNotes: [''],
		tagsCsv: [''],
		imageAssetId: [''],
		foundryId: [''],
	});

	goodsServiceForm = this.fb.group({
		name: ['', [Validators.required, Validators.maxLength(200)]],
		category: [''],
		description: [''],
		gmNotes: [''],
		cost: [''],
		currency: [''],
		availability: [''],
		imageAssetId: [''],
		sourcePoiId: [''],
		foundryId: [''],
	});

	merchantForm = this.fb.group({
		name: ['', [Validators.required, Validators.maxLength(200)]],
		description: [''],
		gmNotes: [''],
		imageAssetId: [''],
		npcId: [''],
		poiId: [''],
		factionId: [''],
		foundryId: [''],
		inventory: this.fb.array([]),
	});

	get merchantInventory(): FormArray {
		return this.merchantForm.get('inventory') as FormArray;
	}

	private makeMerchantInventoryRow(row?: Partial<MerchantInventoryDto>) {
		return this.fb.group({
			goodsId: [row?.goodsId ?? '', [Validators.required]],
			quantity: [row?.quantity ?? null as number | null],
			priceOverride: [row?.priceOverride ?? ''],
			notes: [row?.notes ?? ''],
		});
	}

	addMerchantInventoryRow() {
		this.merchantInventory.push(this.makeMerchantInventoryRow());
	}

	removeMerchantInventoryRow(index: number) {
		this.merchantInventory.removeAt(index);
	}

	vm$: Observable<
		| { kind: 'regions'; title: string }
		| {
				kind: 'points-of-interest';
				title: string;
				regions: Array<{ id: string; name: string }>;
				poiTypes: WorldTypesResponse['pointOfInterestTypes'];
			}
		| {
				kind: 'quests';
				title: string;
				questStatuses: Array<{ id: string; name: string }>;
				factions: Array<{ id: string; name: string }>;
				gods: Array<{ id: string; name: string }>;
				questRequirements?: QuestRequirementDto[];
			}
		| {
				kind: 'tomes';
				title: string;
				pois: Array<{ id: string; name: string; regionName: string | null }>;
				itemRarities: Array<{ id: string; name: string }>;
				tomePages: TomePageDto[];
			}
		| {
				kind: 'tome-pages';
				title: string;
				tomes: Array<{ id: string; name: string }>;
				tomeName?: string | null;
			}
		| {
				kind: 'quest-requirements';
				title: string;
				quests: Array<{ id: string; name: string }>;
				questName?: string | null;
			}
		| {
				kind: 'non-player-characters';
				title: string;
				pois: Array<{ id: string; name: string; regionName: string | null }>;
				factions: Array<{ id: string; name: string }>;
			}
		| {
				kind: 'player-characters';
				title: string;
				characterStatuses: Array<{ id: string; name: string }>;
			}
		| {
				kind: 'factions';
				title: string;
				regions: Array<{ id: string; name: string }>;
				pois: Array<{ id: string; name: string; regionName: string | null }>;
				npcs: Array<{ id: string; name: string }>;
			}
		| { kind: 'gods'; title: string }
		| { kind: 'pantheons'; title: string }
		| { kind: 'domains'; title: string; pantheons: Array<{ id: string; name: string }> }
		| { kind: 'encounters'; title: string; quests: Array<{ id: string; name: string }>; pois: Array<{ id: string; name: string }> }
		| { kind: 'items'; title: string; itemTypes: WorldTypesResponse['itemTypes']; itemRarities: WorldTypesResponse['itemRarities']; pois: Array<{ id: string; name: string }> }
		| { kind: 'goods-services'; title: string; pois: Array<{ id: string; name: string }> }
		| {
				kind: 'merchants';
				title: string;
				npcs: Array<{ id: string; name: string }>;
				pois: Array<{ id: string; name: string }>;
				factions: Array<{ id: string; name: string }>;
				goods: Array<{ id: string; name: string }>;
			}
		| { kind: 'unknown'; title: string }
	>;

	constructor(
		private readonly route: ActivatedRoute,
		private readonly fb: FormBuilder,
		private readonly router: Router,
		private readonly regions: RegionsService,
		private readonly pois: PointsOfInterestService,
		private readonly types: TypesService,
		private readonly quests: QuestsService,
		private readonly tomes: TomesService,
		private readonly factions: FactionsService,
		private readonly gods: GodsService,
		private readonly tomePages: TomePagesService,
		private readonly questRequirements: QuestRequirementsService,
		private readonly npcs: NonPlayerCharactersService,
		private readonly playerCharacters: PlayerCharactersService,
		private readonly pantheons: PantheonsService,
		private readonly domains: DomainsService,
		private readonly encounters: EncountersService,
		private readonly items: ItemsService,
		private readonly goodsServices: GoodsServicesService,
		private readonly merchants: MerchantsService,
		private readonly assetLinks: AssetLinksService
	) {
		this.entity = route.snapshot.paramMap.get('entity') ?? 'entities';
		this.id = route.snapshot.paramMap.get('id');
		this.worldId = route.parent!.snapshot.paramMap.get('worldId') ?? '';

		const assetLinksWorldId$ = route.parent!.paramMap.pipe(map((p) => p.get('worldId') ?? ''));
		const assetLinksEntity$ = route.paramMap.pipe(map((p) => p.get('entity') ?? 'entities'));
		const assetLinksId$ = route.paramMap.pipe(map((p) => p.get('id')));

		this.assetLinksVm$ = combineLatest([
			assetLinksWorldId$,
			assetLinksEntity$,
			assetLinksId$,
			this.assetLinksRefresh$,
		]).pipe(
			switchMap(([worldId, entityType, entityId]) => {
				if (!entityId) return of([] as AssetLinkWithAssetDto[]);
				return this.assetLinks
					.listWithAssets(worldId, { entityType, entityId })
					.pipe(map((res) => res.items ?? []));
			})
		);

		const worldId$ = route.parent!.paramMap.pipe(map((p) => p.get('worldId') ?? ''));
		const entity$ = route.paramMap.pipe(map((p) => p.get('entity') ?? 'entities'));
		const id$ = route.paramMap.pipe(map((p) => p.get('id')));
		const queryTomeId$ = route.queryParamMap.pipe(map((p) => p.get('tomeId')));
		const queryQuestId$ = route.queryParamMap.pipe(map((p) => p.get('questId')));

		this.vm$ = combineLatest([worldId$, entity$, id$, queryTomeId$, queryQuestId$]).pipe(
			switchMap(([worldId, entity, id, queryTomeId, queryQuestId]) => {
				this.worldId = worldId;
				this.entity = entity;
				this.id = id;
				this.queryTomeId = queryTomeId;
				this.queryQuestId = queryQuestId;
				this.refreshAssetLinks();

				if (entity === 'regions') {
					if (id) {
						return this.regions.get(worldId, id).pipe(
							map((res) => res.region),
							map((region: RegionDto) => {
								this.regionForm.patchValue({
									name: region.name,
									description: region.description ?? '',
								});
								return { kind: 'regions' as const, title: 'Region' };
							})
						);
					}

					this.regionForm.patchValue({ name: '', description: '' });
					return of({ kind: 'regions' as const, title: 'New Region' });
				}

				if (entity === 'points-of-interest') {
					const regions$ = this.regions.list(worldId).pipe(map((r) => r.regions.map((x) => ({ id: x.id, name: x.name }))));
					const types$ = this.types.get(worldId).pipe(shareReplay({ bufferSize: 1, refCount: true }));

					const poi$ = id ? this.pois.get(worldId, id).pipe(map((res) => res.poi)) : of(null as PoiDto | null);

					return combineLatest([regions$, types$, poi$]).pipe(
						map(([regions, types, poi]) => {
							if (poi) {
								this.poiForm.patchValue({
									name: poi.name,
									description: poi.description ?? '',
									regionId: poi.regionId,
									typeId: poi.typeId ?? '',
									gmNotes: poi.gmNotes ?? '',
								});
							} else {
								this.poiForm.patchValue({
									name: '',
									description: '',
									regionId: regions[0]?.id ?? '',
									typeId: '',
									gmNotes: '',
								});
							}

							return {
								kind: 'points-of-interest' as const,
								title: poi ? 'Point of Interest' : 'New Point of Interest',
								regions,
								poiTypes: types.pointOfInterestTypes,
							};
						})
					);
				}

				if (entity === 'quests') {
					const types$ = this.types.get(worldId).pipe(shareReplay({ bufferSize: 1, refCount: true }));
					const factions$ = this.factions.list(worldId).pipe(map((r) => r.factions.map((f) => ({ id: f.id, name: f.name }))));
					const gods$ = this.gods.list(worldId).pipe(map((r) => r.gods.map((g) => ({ id: g.id, name: g.name }))));
					const quest$ = id ? this.quests.get(worldId, id).pipe(map((res) => res.quest)) : of(null as QuestDto | null);
					const requirements$ = id
						? this.questRequirements.list(worldId, { questId: id }).pipe(map((r) => r.questRequirements))
						: of([] as QuestRequirementDto[]);

					return combineLatest([types$, factions$, gods$, quest$, requirements$]).pipe(
						map(([types, factions, gods, quest, questRequirements]) => {
							if (quest) {
								this.questForm.patchValue({
									name: quest.name,
									status: quest.status ?? 'planned',
									factionId: quest.factionId ?? '',
									npcId: quest.npcId ?? '',
									godId: quest.godId ?? '',
									description: quest.description ?? '',
									gmNotes: quest.gmNotes ?? '',
									foundryId: quest.foundryId ?? '',
									imageAssetId: quest.imageAssetId ?? '',
									mapAssetId: quest.mapAssetId ?? '',
									pdfAssetId: quest.pdfAssetId ?? '',
								});
							} else {
								this.questForm.patchValue({
									name: '',
									status: 'planned',
									factionId: '',
									npcId: '',
									godId: '',
									description: '',
									gmNotes: '',
									foundryId: '',
									imageAssetId: '',
									mapAssetId: '',
									pdfAssetId: '',
								});
							}

							return {
								kind: 'quests' as const,
								title: quest ? 'Quest' : 'New Quest',
								questStatuses: (types.questStatuses ?? []) as any,
								factions,
								gods,
								questRequirements,
							};
						})
					);
				}

				if (entity === 'tomes') {
					const types$ = this.types.get(worldId).pipe(shareReplay({ bufferSize: 1, refCount: true }));
					const pois$ = this.pois
						.list(worldId)
						.pipe(map((r) => r.pois.map((p) => ({ id: p.id, name: p.name, regionName: p.regionName ?? null }))));
					const tome$ = id ? this.tomes.get(worldId, id).pipe(map((res) => res.tome)) : of(null as TomeDto | null);
					const tomePages$ = id
						? this.tomePages.list(worldId, { tomeId: id }).pipe(map((r) => r.tomePages))
						: of([] as TomePageDto[]);

					return combineLatest([types$, pois$, tome$, tomePages$]).pipe(
						map(([types, pois, tome, tomePages]) => {
							if (tome) {
								this.tomeForm.patchValue({
									name: tome.name,
									description: tome.description ?? '',
									author: tome.author ?? '',
									rarity: tome.rarity ?? '',
									poiId: tome.poiId ?? '',
									foundryId: tome.foundryId ?? '',
									imageAssetId: tome.imageAssetId ?? '',
									mapAssetId: tome.mapAssetId ?? '',
									pdfAssetId: tome.pdfAssetId ?? '',
								});
							} else {
								this.tomeForm.patchValue({
									name: '',
									description: '',
									author: '',
									rarity: '',
									poiId: '',
									foundryId: '',
									imageAssetId: '',
									mapAssetId: '',
									pdfAssetId: '',
								});
							}

							return {
								kind: 'tomes' as const,
								title: tome ? 'Tome' : 'New Tome',
								pois,
								itemRarities: (types.itemRarities ?? []) as any,
								tomePages,
							};
						})
					);
				}

				if (entity === 'tome-pages') {
					const tomes$ = this.tomes.list(worldId).pipe(map((r) => r.tomes.map((t) => ({ id: t.id, name: t.name }))));
					const page$ = id ? this.tomePages.get(worldId, id).pipe(map((r) => r.tomePage)) : of(null as TomePageDto | null);

					return combineLatest([tomes$, page$]).pipe(
						map(([tomes, page]) => {
							if (page) {
								this.tomePageForm.patchValue({
									tomeId: page.tomeId,
									name: page.name,
									content: page.content ?? '',
									foundryId: page.foundryId ?? '',
									imageAssetId: page.imageAssetId ?? '',
								});
								return { kind: 'tome-pages' as const, title: 'Tome Page', tomes, tomeName: page.tomeName ?? null };
							}

							const initialTomeId = queryTomeId ?? tomes[0]?.id ?? '';
							this.tomePageForm.patchValue({
								tomeId: initialTomeId,
								name: '',
								content: '',
								foundryId: '',
								imageAssetId: '',
							});
							return { kind: 'tome-pages' as const, title: 'New Tome Page', tomes };
						})
					);
				}

				if (entity === 'non-player-characters') {
					const pois$ = this.pois
						.list(worldId)
						.pipe(map((r) => r.pois.map((p) => ({ id: p.id, name: p.name, regionName: p.regionName ?? null }))));
					const factions$ = this.factions.list(worldId).pipe(map((r) => r.factions.map((f) => ({ id: f.id, name: f.name }))));
					const npc$ = id ? this.npcs.get(worldId, id).pipe(map((r) => r.npc)) : of(null as NonPlayerCharacterDto | null);

					return combineLatest([pois$, factions$, npc$]).pipe(
						map(([pois, factions, npc]) => {
							if (npc) {
								this.npcForm.patchValue({
									name: npc.name,
									description: npc.description ?? '',
									affiliation: npc.affiliation ?? '',
									homePoiId: npc.homePoiId ?? '',
									factionId: npc.factionId ?? '',
									gmNotes: npc.gmNotes ?? '',
									foundryId: npc.foundryId ?? '',
									imageAssetId: npc.imageAssetId ?? '',
								});
							} else {
								this.npcForm.patchValue({
									name: '',
									description: '',
									affiliation: '',
									homePoiId: '',
									factionId: '',
									gmNotes: '',
									foundryId: '',
									imageAssetId: '',
								});
							}

							return {
								kind: 'non-player-characters' as const,
								title: npc ? 'Non-Player Character' : 'New Non-Player Character',
								pois,
								factions,
							};
						})
					);
				}

				if (entity === 'player-characters') {
					const types$ = this.types.get(worldId).pipe(shareReplay({ bufferSize: 1, refCount: true }));
					const pc$ = id ? this.playerCharacters.get(worldId, id).pipe(map((r) => r.playerCharacter)) : of(null as PlayerCharacterDto | null);

					return combineLatest([types$, pc$]).pipe(
						map(([types, pc]) => {
							if (pc) {
								this.playerCharacterForm.patchValue({
									name: pc.name,
									status: pc.status ?? 'active',
									userId: pc.userId ?? '',
									foundryId: pc.foundryId ?? '',
									imageAssetId: pc.imageAssetId ?? '',
									backstory: pc.backstory ?? '',
									motivation: pc.motivation ?? '',
								});
							} else {
								this.playerCharacterForm.patchValue({
									name: '',
									status: 'active',
									userId: '',
									foundryId: '',
									imageAssetId: '',
									backstory: '',
									motivation: '',
								});
							}

							return {
								kind: 'player-characters' as const,
								title: pc ? 'Player Character' : 'New Player Character',
								characterStatuses: (types.characterStatuses ?? []) as any,
							};
						})
					);
				}

				if (entity === 'factions') {
					const regions$ = this.regions.list(worldId).pipe(map((r) => r.regions.map((x) => ({ id: x.id, name: x.name }))));
					const pois$ = this.pois
						.list(worldId)
						.pipe(map((r) => r.pois.map((p) => ({ id: p.id, name: p.name, regionName: p.regionName ?? null }))));
					const npcs$ = this.npcs.list(worldId).pipe(map((r) => r.npcs.map((n) => ({ id: n.id, name: n.name }))));
					const faction$ = id ? this.factions.get(worldId, id).pipe(map((r) => r.faction)) : of(null as FactionDto | null);

					return combineLatest([regions$, pois$, npcs$, faction$]).pipe(
						map(([regions, pois, npcs, faction]) => {
							if (faction) {
								this.factionForm.patchValue({
									name: faction.name,
									description: faction.description ?? '',
									influence: faction.influence ?? '',
									resources: faction.resources ?? '',
									opposition: faction.opposition ?? '',
									gmNotes: faction.gmNotes ?? '',
									iconAssetId: faction.iconAssetId ?? '',
									foundryId: faction.foundryId ?? '',
									regionIds: faction.regionIds ?? [],
									poiIds: faction.poiIds ?? [],
									npcIds: faction.npcIds ?? [],
								});
							} else {
								this.factionForm.patchValue({
									name: '',
									description: '',
									influence: '',
									resources: '',
									opposition: '',
									gmNotes: '',
									iconAssetId: '',
									foundryId: '',
									regionIds: [],
									poiIds: [],
									npcIds: [],
								});
							}

							return {
								kind: 'factions' as const,
								title: faction ? 'Faction' : 'New Faction',
								regions,
								pois,
								npcs,
							};
						})
					);
				}

				if (entity === 'gods') {
					const god$ = id ? this.gods.get(worldId, id).pipe(map((r) => r.god)) : of(null as GodDto | null);
					return god$.pipe(
						map((god) => {
							if (god) {
								this.godForm.patchValue({
									pantheonId: god.pantheonId,
									name: god.name,
									description: god.description ?? '',
									gmNotes: god.gmNotes ?? '',
									imageAssetId: god.imageAssetId ?? '',
									domainIdsCsv: (god.domainIds ?? []).join(', '),
									foundryId: god.foundryId ?? '',
								});
							} else {
								this.godForm.patchValue({
									pantheonId: '',
									name: '',
									description: '',
									gmNotes: '',
									imageAssetId: '',
									domainIdsCsv: '',
									foundryId: '',
								});
							}

							return { kind: 'gods' as const, title: god ? 'God' : 'New God' };
						})
					);
				}

				if (entity === 'pantheons') {
					const pantheon$ = id ? this.pantheons.get(worldId, id).pipe(map((r) => r.pantheon)) : of(null as PantheonDto | null);
					return pantheon$.pipe(
						map((pantheon) => {
							if (pantheon) {
								this.pantheonForm.patchValue({
									name: pantheon.name,
									currentState: pantheon.currentState ?? '',
									historicalState: pantheon.historicalState ?? '',
									description: pantheon.description ?? '',
									gmNotes: pantheon.gmNotes ?? '',
									iconographyAssetId: pantheon.iconographyAssetId ?? '',
									foundryId: pantheon.foundryId ?? '',
								});
							} else {
								this.pantheonForm.patchValue({
									name: '',
									currentState: '',
									historicalState: '',
									description: '',
									gmNotes: '',
									iconographyAssetId: '',
									foundryId: '',
								});
							}

							return { kind: 'pantheons' as const, title: pantheon ? 'Pantheon' : 'New Pantheon' };
						})
					);
				}

				if (entity === 'domains') {
					const pantheons$ = this.pantheons.list(worldId).pipe(map((r) => r.pantheons.map((p) => ({ id: p.id, name: p.name }))));
					const domain$ = id ? this.domains.get(worldId, id).pipe(map((r) => r.domain)) : of(null as DomainDto | null);

					return combineLatest([pantheons$, domain$]).pipe(
						map(([pantheons, domain]) => {
							if (domain) {
								this.domainForm.patchValue({
									pantheonId: domain.pantheonId,
									name: domain.name,
									description: domain.description ?? '',
									symbol: domain.symbol ?? '',
									imageAssetId: domain.imageAssetId ?? '',
									tenets: domain.tenets ?? '',
									foundryId: domain.foundryId ?? '',
								});
							} else {
								this.domainForm.patchValue({
									pantheonId: pantheons[0]?.id ?? '',
									name: '',
									description: '',
									symbol: '',
									imageAssetId: '',
									tenets: '',
									foundryId: '',
								});
							}

							return { kind: 'domains' as const, title: domain ? 'Domain' : 'New Domain', pantheons };
						})
					);
				}

				if (entity === 'encounters') {
					const quests$ = this.quests.list(worldId).pipe(map((r) => r.quests.map((q) => ({ id: q.id, name: q.name }))));
					const pois$ = this.pois.list(worldId).pipe(map((r) => r.pois.map((p) => ({ id: p.id, name: p.name }))));
					const encounter$ = id ? this.encounters.get(worldId, id).pipe(map((r) => r.encounter)) : of(null as EncounterDto | null);

					return combineLatest([quests$, pois$, encounter$]).pipe(
						map(([quests, pois, encounter]) => {
							if (encounter) {
								this.encounterForm.patchValue({
									name: encounter.name,
									description: encounter.description ?? '',
									gmNotes: encounter.gmNotes ?? '',
									imageAssetId: encounter.imageAssetId ?? '',
									mapAssetId: encounter.mapAssetId ?? '',
									pdfAssetId: encounter.pdfAssetId ?? '',
									environment: encounter.environment ?? '',
									escalation: encounter.escalation ?? '',
									aftermath: encounter.aftermath ?? '',
									foundryId: encounter.foundryId ?? '',
								});
							} else {
								this.encounterForm.patchValue({
									name: '',
									description: '',
									gmNotes: '',
									imageAssetId: '',
									mapAssetId: '',
									pdfAssetId: '',
									environment: '',
									escalation: '',
									aftermath: '',
									foundryId: '',
								});
							}

							return { kind: 'encounters' as const, title: encounter ? 'Encounter' : 'New Encounter', quests, pois };
						})
					);
				}

				if (entity === 'items') {
					const types$ = this.types.get(worldId).pipe(shareReplay({ bufferSize: 1, refCount: true }));
					const pois$ = this.pois.list(worldId).pipe(map((r) => r.pois.map((p) => ({ id: p.id, name: p.name }))));
					const item$ = id ? this.items.get(worldId, id).pipe(map((r) => r.item)) : of(null as ItemDto | null);

					return combineLatest([types$, pois$, item$]).pipe(
						map(([types, pois, item]) => {
							if (item) {
								this.itemForm.patchValue({
									itemTypeId: item.itemTypeId ?? '',
									sourcePoiId: item.sourcePoiId ?? '',
									rarity: item.rarity ?? '',
									name: item.name,
									category: item.category ?? '',
									description: item.description ?? '',
									gmNotes: item.gmNotes ?? '',
									tagsCsv: (item.tags ?? []).join(', '),
									imageAssetId: item.imageAssetId ?? '',
									foundryId: item.foundryId ?? '',
								});
							} else {
								this.itemForm.patchValue({
									itemTypeId: '',
									sourcePoiId: '',
									rarity: '',
									name: '',
									category: '',
									description: '',
									gmNotes: '',
									tagsCsv: '',
									imageAssetId: '',
									foundryId: '',
								});
							}

							return {
								kind: 'items' as const,
								title: item ? 'Item' : 'New Item',
								itemTypes: types.itemTypes ?? [],
								itemRarities: types.itemRarities ?? [],
								pois,
							};
						})
					);
				}

				if (entity === 'goods-services') {
					const pois$ = this.pois.list(worldId).pipe(map((r) => r.pois.map((p) => ({ id: p.id, name: p.name }))));
					const goods$ = id ? this.goodsServices.get(worldId, id).pipe(map((r) => r.goods)) : of(null as GoodsServiceDto | null);

					return combineLatest([pois$, goods$]).pipe(
						map(([pois, goods]) => {
							if (goods) {
								this.goodsServiceForm.patchValue({
									name: goods.name,
									category: goods.category ?? '',
									description: goods.description ?? '',
									gmNotes: goods.gmNotes ?? '',
									cost: goods.cost ?? '',
									currency: goods.currency ?? '',
									availability: goods.availability ?? '',
									imageAssetId: goods.imageAssetId ?? '',
									sourcePoiId: goods.sourcePoiId ?? '',
									foundryId: goods.foundryId ?? '',
								});
							} else {
								this.goodsServiceForm.patchValue({
									name: '',
									category: '',
									description: '',
									gmNotes: '',
									cost: '',
									currency: '',
									availability: '',
									imageAssetId: '',
									sourcePoiId: '',
									foundryId: '',
								});
							}

							return {
								kind: 'goods-services' as const,
								title: goods ? 'Goods & Service' : 'New Goods & Service',
								pois,
							};
						})
					);
				}

				if (entity === 'merchants') {
					const npcs$ = this.npcs.list(worldId).pipe(map((r) => r.npcs.map((n) => ({ id: n.id, name: n.name }))));
					const pois$ = this.pois.list(worldId).pipe(map((r) => r.pois.map((p) => ({ id: p.id, name: p.name }))));
					const factions$ = this.factions.list(worldId).pipe(map((r) => r.factions.map((f) => ({ id: f.id, name: f.name }))));
					const goods$ = this.goodsServices
						.list(worldId, { limit: 500 })
						.pipe(map((r) => r.goods.map((g) => ({ id: g.id, name: g.name }))));
					const merchant$ = id ? this.merchants.get(worldId, id) : of(null as { merchant: MerchantDto; inventory: MerchantInventoryDto[] } | null);

					return combineLatest([npcs$, pois$, factions$, goods$, merchant$]).pipe(
						map(([npcs, pois, factions, goods, res]) => {
							const merchant = res?.merchant ?? null;
							const inventory = res?.inventory ?? [];

							if (merchant) {
								this.merchantForm.patchValue({
									name: merchant.name,
									description: merchant.description ?? '',
									gmNotes: merchant.gmNotes ?? '',
									imageAssetId: merchant.imageAssetId ?? '',
									npcId: merchant.npcId ?? '',
									poiId: merchant.poiId ?? '',
									factionId: merchant.factionId ?? '',
									foundryId: merchant.foundryId ?? '',
								});
							} else {
								this.merchantForm.patchValue({
									name: '',
									description: '',
									gmNotes: '',
									imageAssetId: '',
									npcId: '',
									poiId: '',
									factionId: '',
									foundryId: '',
								});
							}

							while (this.merchantInventory.length > 0) {
								this.merchantInventory.removeAt(0);
							}
							for (const row of inventory) {
								this.merchantInventory.push(this.makeMerchantInventoryRow(row));
							}
							if (!merchant && this.merchantInventory.length === 0) {
								this.merchantInventory.push(this.makeMerchantInventoryRow());
							}

							return {
								kind: 'merchants' as const,
								title: merchant ? 'Merchant' : 'New Merchant',
								npcs,
								pois,
								factions,
								goods,
							};
						})
					);
				}

				if (entity === 'quest-requirements') {
					const quests$ = this.quests.list(worldId).pipe(map((r) => r.quests.map((q) => ({ id: q.id, name: q.name }))));
					const requirement$ = id
						? this.questRequirements.get(worldId, id).pipe(map((r) => r.questRequirement))
						: of(null as QuestRequirementDto | null);

					return combineLatest([quests$, requirement$]).pipe(
						map(([quests, requirement]) => {
							if (requirement) {
								this.questRequirementForm.patchValue({
									questId: requirement.questId,
									name: requirement.name,
									description: requirement.description ?? '',
									status: requirement.status ?? 'Not Known',
									foundryId: requirement.foundryId ?? '',
									imageAssetId: requirement.imageAssetId ?? '',
								});
								return {
									kind: 'quest-requirements' as const,
									title: 'Quest Requirement',
									quests,
									questName: requirement.questName ?? null,
								};
							}

							const initialQuestId = queryQuestId ?? quests[0]?.id ?? '';
							this.questRequirementForm.patchValue({
								questId: initialQuestId,
								name: '',
								description: '',
								status: 'Not Known',
								foundryId: '',
								imageAssetId: '',
							});

							return {
								kind: 'quest-requirements' as const,
								title: 'New Quest Requirement',
								quests,
							};
						})
					);
				}

				return of({ kind: 'unknown' as const, title: entity.replace(/-/g, ' ') });
			})
		);
	}

	refreshAssetLinks() {
		this.assetLinksRefresh$.next();
	}

	addAssetLink() {
		if (!this.id) return;
		if (this.assetLinkForm.invalid) return;
		this.savingAssetLink = true;
		const v = this.assetLinkForm.value;
		this.assetLinks
			.create(this.worldId, {
				entityType: this.entity,
				entityId: this.id,
				assetId: v.assetId!,
				usage: v.usage!,
				sort: v.sort ?? 0,
			})
			.subscribe({
				next: () => {
					this.savingAssetLink = false;
					this.assetLinkForm.patchValue({ assetId: '', sort: 0 });
					this.refreshAssetLinks();
				},
				error: () => {
					this.savingAssetLink = false;
				},
			});
	}

	unlinkAsset(linkId: string) {
		this.assetLinks.delete(this.worldId, linkId).subscribe({
			next: () => this.refreshAssetLinks(),
		});
	}

	cancel() {
		if (this.entity === 'tome-pages' && this.queryTomeId) {
			void this.router.navigate(['/workspace/worlds', this.worldId, this.entity], {
				queryParams: { tomeId: this.queryTomeId },
			});
			return;
		}
		if (this.entity === 'quest-requirements' && this.queryQuestId) {
			void this.router.navigate(['/workspace/worlds', this.worldId, this.entity], {
				queryParams: { questId: this.queryQuestId },
			});
			return;
		}
		void this.router.navigate(['/workspace/worlds', this.worldId, this.entity]);
	}

	saveRegion() {
		if (this.regionForm.invalid || this.saving) return;
		this.saving = true;
		const value = this.regionForm.getRawValue();
		const payload = {
			name: value.name!,
			description: value.description ?? '',
		};

		const req$ = this.id
			? this.regions.update(this.worldId, this.id, payload)
			: this.regions.create(this.worldId, payload);

		req$.subscribe({
			next: (res) => {
				this.saving = false;
				void this.router.navigate(['/workspace/worlds', this.worldId, 'regions', res.region.id]);
			},
			error: () => {
				this.saving = false;
			},
		});
	}

	savePoi() {
		if (this.poiForm.invalid || this.saving) return;
		this.saving = true;
		const value = this.poiForm.getRawValue();
		const payload = {
			name: value.name!,
			description: value.description ?? '',
			regionId: value.regionId!,
			typeId: value.typeId ? value.typeId : null,
			gmNotes: value.gmNotes ?? null,
		};

		const req$ = this.id
			? this.pois.update(this.worldId, this.id, payload)
			: this.pois.create(this.worldId, payload);

		req$.subscribe({
			next: (res) => {
				this.saving = false;
				void this.router.navigate(['/workspace/worlds', this.worldId, 'points-of-interest', res.poi.id]);
			},
			error: () => {
				this.saving = false;
			},
		});
	}

	saveQuest() {
		if (this.questForm.invalid || this.saving) return;
		this.saving = true;
		const value = this.questForm.getRawValue();
		const payload = {
			name: value.name!,
			status: value.status ? value.status : null,
			factionId: value.factionId ? value.factionId : null,
			npcId: value.npcId ? value.npcId : null,
			godId: value.godId ? value.godId : null,
			description: value.description ?? null,
			gmNotes: value.gmNotes ?? null,
			foundryId: value.foundryId ? value.foundryId : null,
			imageAssetId: value.imageAssetId ? value.imageAssetId : null,
			mapAssetId: value.mapAssetId ? value.mapAssetId : null,
			pdfAssetId: value.pdfAssetId ? value.pdfAssetId : null,
		};

		const req$ = this.id
			? this.quests.update(this.worldId, this.id, payload)
			: this.quests.create(this.worldId, payload);

		req$.subscribe({
			next: (res) => {
				this.saving = false;
				void this.router.navigate(['/workspace/worlds', this.worldId, 'quests', res.quest.id]);
			},
			error: () => {
				this.saving = false;
			},
		});
	}

	saveTome() {
		if (this.tomeForm.invalid || this.saving) return;
		this.saving = true;
		const value = this.tomeForm.getRawValue();
		const payload = {
			name: value.name!,
			description: value.description ?? null,
			author: value.author ? value.author : null,
			rarity: value.rarity ? value.rarity : null,
			poiId: value.poiId ? value.poiId : null,
			foundryId: value.foundryId ? value.foundryId : null,
			imageAssetId: value.imageAssetId ? value.imageAssetId : null,
			mapAssetId: value.mapAssetId ? value.mapAssetId : null,
			pdfAssetId: value.pdfAssetId ? value.pdfAssetId : null,
		};

		const req$ = this.id
			? this.tomes.update(this.worldId, this.id, payload)
			: this.tomes.create(this.worldId, payload);

		req$.subscribe({
			next: (res) => {
				this.saving = false;
				void this.router.navigate(['/workspace/worlds', this.worldId, 'tomes', res.tome.id]);
			},
			error: () => {
				this.saving = false;
			},
		});
	}

	saveTomePage() {
		if (this.tomePageForm.invalid || this.saving) return;
		this.saving = true;
		const value = this.tomePageForm.getRawValue();
		const payload = {
			tomeId: value.tomeId!,
			name: value.name!,
			content: value.content ?? null,
			foundryId: value.foundryId ? value.foundryId : null,
			imageAssetId: value.imageAssetId ? value.imageAssetId : null,
		};

		const req$ = this.id
			? this.tomePages.update(this.worldId, this.id, payload)
			: this.tomePages.create(this.worldId, payload);

		req$.subscribe({
			next: (res) => {
				this.saving = false;
				void this.router.navigate(['/workspace/worlds', this.worldId, 'tome-pages', res.tomePage.id]);
			},
			error: () => {
				this.saving = false;
			},
		});
	}

	saveQuestRequirement() {
		if (this.questRequirementForm.invalid || this.saving) return;
		this.saving = true;
		const value = this.questRequirementForm.getRawValue();
		const payload = {
			questId: value.questId!,
			name: value.name!,
			description: value.description ?? null,
			status: value.status ? value.status : null,
			foundryId: value.foundryId ? value.foundryId : null,
			imageAssetId: value.imageAssetId ? value.imageAssetId : null,
		};

		const req$ = this.id
			? this.questRequirements.update(this.worldId, this.id, payload)
			: this.questRequirements.create(this.worldId, payload);

		req$.subscribe({
			next: (res) => {
				this.saving = false;
				void this.router.navigate(['/workspace/worlds', this.worldId, 'quest-requirements', res.questRequirement.id]);
			},
			error: () => {
				this.saving = false;
			},
		});
	}

	saveNpc() {
		if (this.npcForm.invalid || this.saving) return;
		this.saving = true;
		const value = this.npcForm.getRawValue();
		const payload = {
			name: value.name!,
			description: value.description ?? null,
			affiliation: value.affiliation ? value.affiliation : null,
			homePoiId: value.homePoiId ? value.homePoiId : null,
			factionId: value.factionId ? value.factionId : null,
			gmNotes: value.gmNotes ?? null,
			foundryId: value.foundryId ? value.foundryId : null,
			imageAssetId: value.imageAssetId ? value.imageAssetId : null,
		};

		const req$ = this.id
			? this.npcs.update(this.worldId, this.id, payload)
			: this.npcs.create(this.worldId, payload);

		req$.subscribe({
			next: (res) => {
				this.saving = false;
				void this.router.navigate(['/workspace/worlds', this.worldId, 'non-player-characters', res.npc.id]);
			},
			error: () => {
				this.saving = false;
			},
		});
	}

	savePlayerCharacter() {
		if (this.playerCharacterForm.invalid || this.saving) return;
		this.saving = true;
		const value = this.playerCharacterForm.getRawValue();
		const payload = {
			name: value.name!,
			status: value.status ? value.status : null,
			userId: value.userId ? value.userId : null,
			foundryId: value.foundryId ? value.foundryId : null,
			imageAssetId: value.imageAssetId ? value.imageAssetId : null,
			backstory: value.backstory ?? null,
			motivation: value.motivation ?? null,
		};

		const req$ = this.id
			? this.playerCharacters.update(this.worldId, this.id, payload)
			: this.playerCharacters.create(this.worldId, payload);

		req$.subscribe({
			next: (res) => {
				this.saving = false;
				void this.router.navigate(['/workspace/worlds', this.worldId, 'player-characters', res.playerCharacter.id]);
			},
			error: () => {
				this.saving = false;
			},
		});
	}

	saveFaction() {
		if (this.factionForm.invalid || this.saving) return;
		this.saving = true;
		const value = this.factionForm.getRawValue();
		const payload = {
			name: value.name!,
			description: value.description ?? null,
			influence: value.influence ?? null,
			resources: value.resources ?? null,
			opposition: value.opposition ?? null,
			gmNotes: value.gmNotes ?? null,
			iconAssetId: value.iconAssetId ? value.iconAssetId : null,
			foundryId: value.foundryId ? value.foundryId : null,
			regionIds: (value.regionIds ?? []).filter((x) => Boolean(x)),
			poiIds: (value.poiIds ?? []).filter((x) => Boolean(x)),
			npcIds: (value.npcIds ?? []).filter((x) => Boolean(x)),
		};

		const req$ = this.id
			? this.factions.update(this.worldId, this.id, payload)
			: this.factions.create(this.worldId, payload);

		req$.subscribe({
			next: (res) => {
				this.saving = false;
				void this.router.navigate(['/workspace/worlds', this.worldId, 'factions', res.faction.id]);
			},
			error: () => {
				this.saving = false;
			},
		});
	}

	saveGod() {
		if (this.godForm.invalid || this.saving) return;
		this.saving = true;
		const value = this.godForm.getRawValue();
		const domainIds = (value.domainIdsCsv ?? '')
			.split(/[,\n]/g)
			.map((x) => x.trim())
			.filter((x) => Boolean(x));
		const payload = {
			pantheonId: value.pantheonId!,
			name: value.name!,
			description: value.description ?? null,
			gmNotes: value.gmNotes ?? null,
			imageAssetId: value.imageAssetId ? value.imageAssetId : null,
			domainIds,
			foundryId: value.foundryId ? value.foundryId : null,
		};

		const req$ = this.id
			? this.gods.update(this.worldId, this.id, payload)
			: this.gods.create(this.worldId, payload);

		req$.subscribe({
			next: (res) => {
				this.saving = false;
				void this.router.navigate(['/workspace/worlds', this.worldId, 'gods', res.god.id]);
			},
			error: () => {
				this.saving = false;
			},
		});
	}

	savePantheon() {
		if (this.pantheonForm.invalid || this.saving) return;
		this.saving = true;
		const value = this.pantheonForm.getRawValue();
		const payload = {
			name: value.name!,
			currentState: value.currentState ?? null,
			historicalState: value.historicalState ?? null,
			description: value.description ?? null,
			gmNotes: value.gmNotes ?? null,
			iconographyAssetId: value.iconographyAssetId ? value.iconographyAssetId : null,
			foundryId: value.foundryId ? value.foundryId : null,
		};

		const req$ = this.id
			? this.pantheons.update(this.worldId, this.id, payload)
			: this.pantheons.create(this.worldId, payload);

		req$.subscribe({
			next: (res) => {
				this.saving = false;
				void this.router.navigate(['/workspace/worlds', this.worldId, 'pantheons', res.pantheon.id]);
			},
			error: () => {
				this.saving = false;
			},
		});
	}

	saveDomain() {
		if (this.domainForm.invalid || this.saving) return;
		this.saving = true;
		const value = this.domainForm.getRawValue();
		const payload = {
			pantheonId: value.pantheonId!,
			name: value.name!,
			description: value.description ?? null,
			symbol: value.symbol ?? null,
			imageAssetId: value.imageAssetId ?? null,
			tenets: value.tenets ?? null,
			foundryId: value.foundryId ?? null,
		};

		const req$ = this.id
			? this.domains.update(this.worldId, this.id, payload)
			: this.domains.create(this.worldId, payload);

		req$.subscribe({
			next: (res) => {
				this.saving = false;
				void this.router.navigate(['/workspace/worlds', this.worldId, 'domains', res.domain.id]);
			},
			error: () => {
				this.saving = false;
			},
		});
	}

	saveEncounter() {
		if (this.encounterForm.invalid || this.saving) return;
		this.saving = true;
		const value = this.encounterForm.getRawValue();
		const payload = {
			name: value.name!,
			description: value.description ?? null,
			gmNotes: value.gmNotes ?? null,
			imageAssetId: value.imageAssetId ?? null,
			mapAssetId: value.mapAssetId ?? null,
			pdfAssetId: value.pdfAssetId ?? null,
			environment: value.environment ?? null,
			escalation: value.escalation ?? null,
			aftermath: value.aftermath ?? null,
			foundryId: value.foundryId ?? null,
		};

		const req$ = this.id
			? this.encounters.update(this.worldId, this.id, payload)
			: this.encounters.create(this.worldId, payload);

		req$.subscribe({
			next: (res) => {
				this.saving = false;
				void this.router.navigate(['/workspace/worlds', this.worldId, 'encounters', res.encounter.id]);
			},
			error: () => {
				this.saving = false;
			},
		});
	}

	saveItem() {
		if (this.itemForm.invalid || this.saving) return;
		this.saving = true;
		const value = this.itemForm.getRawValue();
		const tags = (value.tagsCsv ?? '')
			.split(',')
			.map((t) => t.trim())
			.filter((t) => t.length > 0);
		const payload = {
			name: value.name!,
			itemTypeId: value.itemTypeId ? value.itemTypeId : null,
			sourcePoiId: value.sourcePoiId ? value.sourcePoiId : null,
			rarity: value.rarity ? value.rarity : null,
			category: value.category ?? null,
			description: value.description ?? null,
			gmNotes: value.gmNotes ?? null,
			tags,
			imageAssetId: value.imageAssetId ?? null,
			foundryId: value.foundryId ?? null,
		};

		const req$ = this.id
			? this.items.update(this.worldId, this.id, payload)
			: this.items.create(this.worldId, payload);

		req$.subscribe({
			next: (res) => {
				this.saving = false;
				void this.router.navigate(['/workspace/worlds', this.worldId, 'items', res.item.id]);
			},
			error: () => {
				this.saving = false;
			},
		});
	}

	saveGoodsService() {
		if (this.goodsServiceForm.invalid || this.saving) return;
		this.saving = true;
		const value = this.goodsServiceForm.getRawValue();
		const payload = {
			name: value.name!,
			category: value.category ? value.category : null,
			description: value.description ? value.description : null,
			gmNotes: value.gmNotes ? value.gmNotes : null,
			cost: value.cost ? value.cost : null,
			currency: value.currency ? value.currency : null,
			availability: value.availability ? value.availability : null,
			imageAssetId: value.imageAssetId ? value.imageAssetId : null,
			sourcePoiId: value.sourcePoiId ? value.sourcePoiId : null,
			foundryId: value.foundryId ? value.foundryId : null,
		};

		const req$ = this.id
			? this.goodsServices.update(this.worldId, this.id, payload)
			: this.goodsServices.create(this.worldId, payload);

		req$.subscribe({
			next: (res) => {
				this.saving = false;
				void this.router.navigate(['/workspace/worlds', this.worldId, 'goods-services', res.goods.id]);
			},
			error: () => {
				this.saving = false;
			},
		});
	}

	saveMerchant() {
		if (this.merchantForm.invalid || this.saving) return;
		this.saving = true;
		const value = this.merchantForm.getRawValue() as any;
		const payload = {
			name: value.name!,
			description: value.description ? value.description : null,
			gmNotes: value.gmNotes ? value.gmNotes : null,
			imageAssetId: value.imageAssetId ? value.imageAssetId : null,
			npcId: value.npcId ? value.npcId : null,
			poiId: value.poiId ? value.poiId : null,
			factionId: value.factionId ? value.factionId : null,
			foundryId: value.foundryId ? value.foundryId : null,
			inventory: (value.inventory ?? []).map((i: any) => ({
				goodsId: i.goodsId,
				quantity: i.quantity === null || i.quantity === undefined || i.quantity === '' ? null : Number(i.quantity),
				priceOverride: i.priceOverride ? i.priceOverride : null,
				notes: i.notes ? i.notes : null,
			})),
		};

		const req$ = this.id
			? this.merchants.update(this.worldId, this.id, payload)
			: this.merchants.create(this.worldId, payload);

		req$.subscribe({
			next: (res) => {
				this.saving = false;
				void this.router.navigate(['/workspace/worlds', this.worldId, 'merchants', res.merchant.id]);
			},
			error: () => {
				this.saving = false;
			},
		});
	}

	openNewTomePage() {
		if (!this.id) return;
		void this.router.navigate(['/workspace/worlds', this.worldId, 'tome-pages', 'new'], {
			queryParams: { tomeId: this.id },
		});
	}

	openTomePage(tomePageId: string) {
		void this.router.navigate(['/workspace/worlds', this.worldId, 'tome-pages', tomePageId]);
	}

	openNewQuestRequirement() {
		if (!this.id) return;
		void this.router.navigate(['/workspace/worlds', this.worldId, 'quest-requirements', 'new'], {
			queryParams: { questId: this.id },
		});
	}

	openQuestRequirement(requirementId: string) {
		void this.router.navigate(['/workspace/worlds', this.worldId, 'quest-requirements', requirementId]);
	}
}
